
import { useFetchDomainVisibilityAnalysisProcessQuery } from '../../service';
import { useEffect, useState } from 'react';
import { ModelFamily } from '../../types';
import { ProcessStatus } from '../../../process/types';


// A process that is still working. Anything else - finished, failed, aborted, or a status this
// client has never heard of - is over, so polling stops instead of running for ever.
const RUNNING_STATUSES: ProcessStatus[] = ['pending', 'running'];


export default function useVisibilityAnalysisProgress(domain: string, modelFamily: ModelFamily) {
    const [hasActiveProcess, setHasActiveProcess] = useState<boolean>(false);
    const [processGuid, setProcessGuid] = useState<string | null>(null);

    const { data: domainVisibilityAnalysisProcess, isLoading, error, isError } = useFetchDomainVisibilityAnalysisProcessQuery(
        { domain, modelFamily, processGuid },
        {
            pollingInterval: hasActiveProcess ? 10_000 : 0,
            refetchOnMountOrArgChange: true,
        }
    );
    const analysisError = domainVisibilityAnalysisProcess?.output?.error || null;

    useEffect(() => {
        if (!domainVisibilityAnalysisProcess) return;

        const isRunning = RUNNING_STATUSES.includes(domainVisibilityAnalysisProcess.status);

        // We didn't have an active process but now we do
        if (isRunning && hasActiveProcess === false) {
            setHasActiveProcess(true);
            setProcessGuid(domainVisibilityAnalysisProcess.guid);
            return;
        }

        // Active process is over
        if (!isRunning && hasActiveProcess === true) {
            setHasActiveProcess(false);

            // The guid is dropped only when the analysis completed, because asking again without
            // it makes the API start a new analysis when the domain has none stored - which is
            // exactly the case after a failed first run, and it would restart itself for ever.
            if (domainVisibilityAnalysisProcess.status === 'completed') {
                setProcessGuid(null);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainVisibilityAnalysisProcess]);

    // Watch the process the Refresh button has just started. Without this the hook would keep
    // reading the previous process and never notice the new one.
    const trackAnalysisProcess = (guid: string) => {
        setProcessGuid(guid);
        setHasActiveProcess(true);
    };

    return {
        analysisError: analysisError,
        isLoading,
        error,
        isError,
        hasActiveProcess,
        trackAnalysisProcess
    };
}
